/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-console */
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { ErrorMessage } from '@hookform/error-message';
import useApi from '../hooks/useApi';
import useShopifyApi from '../hooks/useShopifyApi';
import useListenSize from '../hooks/useListenSize';
import { segmentEvent } from '../helpers/sendEvent';
import phoneMask from '../helpers/phoneMask';
import {
  addVwoDecision,
  addCheckoutAll,
  addPayFrequencyLookup,
  changeFeatureLinearFlow,
  addVerificationCheckoutUid,
  addVerificationCode,
  sendError,
  addAaronsReferral,
  addNewUserDetails,
} from '../redux/actions';
import { isRenderableApproval } from '../helpers/aaronsReferral';
import ErrorHandling from '../components/ErrorHandling/index';
import SubmitButton from '../components/SubmitButton';

// The options the Your income screen offers, so only a value it can show is pre-selected.
const PAY_FREQUENCIES = new Set(['weekly', 'bi-weekly', 'monthly', 'semi-monthly']);
const INCOME_PERIODS = new Set(['year', 'paycheck']);

// The customer's profile from customer_phone_lookup, in the newUserDetails shape the
// About you and Your income screens pre-fill from. Income fields are only copied when the
// lookup has them and they fit those screens' options, so nothing invalid is pre-selected.
const getInstoreProfileDetails = (lookup, verifiedPhone) => {
  const frequency = String(lookup.payment_frequency || lookup.pay_frequency || '').toLowerCase();
  const income = {
    income: lookup.income,
    per_period: INCOME_PERIODS.has(lookup.per_period) ? lookup.per_period : undefined,
    payment_frequency: PAY_FREQUENCIES.has(frequency) ? frequency : undefined,
    last_pay_date: lookup.last_pay_date,
    direct_deposit: lookup.direct_deposit,
  };
  return {
    billing_first_name: lookup.first_name,
    billing_last_name: lookup.last_name,
    email: lookup.email,
    phone: lookup.phone || verifiedPhone,
    billing_address: lookup.address,
    billing_address2: lookup.address2,
    billing_city: lookup.city,
    billing_state: lookup.state,
    billing_zip: lookup.zip,
    ...Object.fromEntries(Object.entries(income).filter(([, value]) => value !== undefined && value !== null)),
  };
};

function ReturnUserFlow() {
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm();

  const dispatch = useDispatch();
  const { request, response, loading, error } = useApi();
  const { request: shopifyRequest, response: shopifyResponse, loading: shopifyLoading } = useShopifyApi();
  const store = useSelector(state => ({
    vwo: state.loanInfo?.vwo,
    phone: state.contact?.phone,
    firstName: state.personal?.firstName,
    uid: state.loanInfo?.uid,
    availableLimit: state.loanInfo?.availableLimit,
    shopify: state.technical?.shopify,
    checkoutLookupData: state.personal?.checkoutLookupData,
    newUserDetails: state.personal?.newUserDetails,
    featureGates: state.technical?.featureGates,
    DEdgeToken: state.technical?.DEdgeToken,
    isInstoreVccFlow: state.technical?.instoreVccFlow?.isInstoreVccFlow,
    instoreStoreId: state.technical?.instoreVccFlow?.storeId,
  }));

  const [resend, setResend] = useState(true);
  const [code, setCode] = useState('');
  const [ssn4, setSsn4] = useState('');
  // const [emailResend, setEmailResend] = useState(true);
  const [smsMarketing, setSmsMarketing] = useState(true);
  const [emailMarketing, setEmailMarketing] = useState(true);
  const history = useHistory();
  useListenSize();

  // The in-store flow enters the SMS code here instead of on VerificationCodeSubmission, which is where the
  // 'Mobile Verification' page event fires for the other flows.
  useEffect(() => {
    if (store.isInstoreVccFlow) {
      segmentEvent('page', 'Mobile Verification', { flow: 'instore_vcc', store_id: store.instoreStoreId });
    }
  }, []);

  const resendValidationCode = async () => {
    setResend(false);
    await request.loginRequest({ phone: store.phone });
    setTimeout(() => setResend(true), 10000);
  };

  // const resendValidationCodeToEmail = async () => {
  //   setEmailResend(false);
  //   await request.sendCodeToEmailRequest({ phone: store.phone });
  //   setTimeout(() => setEmailResend(true), 10000);
  // };

  const onChangeNumber = async () => {
    history.push('/new-user-flow');
  };

  const initializeRequest = async () => {
    const payload = {
      ...store.checkoutLookupData?.checkout_object,
      phone: store.phone,
    };

    if (payload.customer && payload.customer.billing && payload.customer.shipping) {
      payload.customer.billing.phone = store.phone;
      payload.customer.shipping.phone = store.phone;
    }

    const cartCheck = await shopifyRequest?.cartCheck(payload);
    if (cartCheck?.is_valid === false) {
      const exceededBy = parseFloat(cartCheck.cart_total - cartCheck.available_limit, 10).toFixed(2);
      const message = `Shopping cart exceeds available limit by $${exceededBy}.
      Please remove an item(s) in order to proceed to checkout.`;
      dispatch(sendError({ message }));
      dispatch(addCheckoutAll(cartCheck));
      if (store.featureGates?.EnableDownpayment) {
        history.push('/exceeded-limit-lcr');
      } else {
        history.push('/exceeded-limit');
      }
    } else if (cartCheck?.downpayment) {
      const exceededBy = parseFloat(cartCheck.cart_total - cartCheck.available_limit, 10).toFixed(2);
      const message = `You have exceeded your available limit by $${exceededBy}`;
      dispatch(sendError({ message }));
      dispatch(addCheckoutAll(cartCheck));
      if (store.featureGates?.EnableDownpayment) {
        history.push('/exceeded-limit-lcr');
      } else {
        history.push('/exceeded-limit');
      }
    } else {
      const initPayload = { ...payload, dedge_token: store.DEdgeToken, code };
      await shopifyRequest.initializeRequest(initPayload);
      const data = await shopifyResponse.json();
      if (shopifyResponse.ok && data?.new_decision?.approved === false) {
        if (isRenderableApproval(data.aarons)) {
          dispatch(addAaronsReferral(data.aarons));
        }
        history.push('/not-approved');
      } else if (shopifyResponse.ok) {
        dispatch(addCheckoutAll(data));
        dispatch(
          addPayFrequencyLookup({
            first_name: store.checkoutLookupData?.checkout_object?.customer?.billing?.first_name,
          }),
        );
        dispatch(
          addVwoDecision({
            feature: 'offerPage',
            variation: 'Version-B',
          }),
        );
        dispatch(
          addVwoDecision({
            feature: 'checkoutPage',
            variation: 'Version-A',
          }),
        );
        dispatch(changeFeatureLinearFlow(false));
        dispatch(addVerificationCheckoutUid(data.uid));
        if (data?.reason?.includes('existing non-performing lease')) {
          history.push('/due-payment');
        } else {
          history.push('/display-offered-lease-details');
        }
      } else if (data?.message?.includes('Shopping cart exceeds available limit')) {
        dispatch(sendError(data));
        history.push('/exceeded-limit');
      } else {
        history.push('/error');
      }
    }
  };

  const handleAddressMismatch = async () => {
    const phonePayload = {
      phone: store.phone,
      ssn4,
    };
    const phoneLookupResponse = await shopifyRequest.customerPhoneLookup(phonePayload);
    if (phoneLookupResponse.error) {
      console.log('inside phoneLookupResponse.error');
      setError('ssn', {
        type: 'server',
        message: phoneLookupResponse?.error?.message,
      });
    } else {
      dispatch(addCheckoutAll(phoneLookupResponse));
      history.push('/address-check');
    }
  };

  // In-store VCC builds the cart after approval, so there is no address check, cart check or initialize
  // here. The phone lookup alone says whether the customer still has an approval they can use.
  const handleInstoreReturnUser = async () => {
    const phoneLookupResponse = await shopifyRequest.customerPhoneLookup({ phone: store.phone, ssn4 });
    if (!phoneLookupResponse || phoneLookupResponse.error) {
      setError('ssn', {
        type: 'server',
        message: phoneLookupResponse?.error?.message || 'Unable to verify your details. Please try again.',
      });
      return;
    }
    dispatch(addCheckoutAll(phoneLookupResponse));

    if (!phoneLookupResponse.usable) {
      dispatch(
        addNewUserDetails({ ...getInstoreProfileDetails(phoneLookupResponse, store.phone), available_limit: null }),
      );
      history.push('/new-user-details');
      return;
    }

    dispatch(
      addNewUserDetails({
        ...getInstoreProfileDetails(phoneLookupResponse, store.phone),
        available_limit: phoneLookupResponse.available_limit,
      }),
    );
    // Already approved, so skip the congratulations screen and go straight to building the
    // cart. The cart screen reads the limit set just above.
    history.push('/approved-with-cart');
  };

  const handleReturnUserVerification = async () => {
    if (!store.shopify?.isReturnUserFlow) {
      history.push('/new-user-details');
      return;
    }
    if (store.isInstoreVccFlow) {
      await handleInstoreReturnUser();
      return;
    }
    const payload = {
      ...store.checkoutLookupData?.checkout_object,
      phone: store.phone,
      code,
    };
    const addressCheckResponse = await shopifyRequest.addressCheck(payload);
    if (addressCheckResponse.mismatch) {
      await handleAddressMismatch();
    } else {
      initializeRequest();
    }
  };

  const onSubmit = async () => {
    if (store.isInstoreVccFlow) {
      segmentEvent('track', 'Mobile Verification Submitted', { flow: 'instore_vcc', store_id: store.instoreStoreId });
      segmentEvent('track', 'Instore New user flow enter code screen', {
        store_id: store.instoreStoreId,
      });
    } else {
      segmentEvent('track', 'Shopify New user flow enter code screen', {
        available_limit: store.availableLimit,
      });
    }
    await shopifyRequest.authVerifyRequest({ phone: store.phone, code });
    const data = await shopifyResponse.json();

    if (shopifyResponse.ok && data?.rejected) {
      history.push('/not-approved');
    } else if (shopifyResponse.ok) {
      dispatch(addVerificationCode(code));
      await handleReturnUserVerification();
    } else {
      setError('code', {
        type: 'server',
        message: data?.error?.message,
      });
    }
  };

  return (
    <div className="k-content-wrapper">
      <ErrorHandling response={response} error={error} />
      <section className="k-info k-return-user-info">
        <h1 className="synchrony-main-title k-shopify-header">We just sent you a text</h1>
      </section>
      <section className="k-info k-new-user-subtitle">
        <span className="k-new-user-subtitle-text">Enter the verification code sent to {phoneMask(store.phone)}</span>
        <button type="button" className="k-change-number" onClick={onChangeNumber}>
          Change number
        </button>
      </section>
      <section className="k-form k-form-indent">
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="k-field animate__animated animate__fadeInDown">
            <button
              type="button"
              className="k-btn-link k-btn-link-inside-input"
              disabled={!resend}
              onClick={resendValidationCode}
            >
              {resend ? 'Re-send' : 'Code sent'}
            </button>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="code">Enter 6-digit code</label>
            <input
              type="tel"
              name="code"
              maxLength="6"
              id="code"
              autoComplete="off"
              placeholder="123456"
              {...register('code', {
                required: 'This is required',
                onChange: e => setCode(e.target.value),
                pattern: {
                  value: /\d{6}/,
                  message: 'Should be 6 numeric digits ',
                },
              })}
            />
            <div className="k-form-validation-message">
              <ErrorMessage errors={errors} name="code" render={({ message }) => message} />
            </div>
          </div>
          <div>
            {store.shopify?.isReturnUserFlow && (
              <div className="k-field animate__animated animate__fadeInDown">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="ssn">Last 4 digits of SSN / ITIN</label>
                <input
                  type="tel"
                  name="ssn"
                  maxLength="4"
                  id="ssn"
                  autoComplete="off"
                  placeholder="1234"
                  className="fs-exclude"
                  {...register('ssn', {
                    required: 'This is required',
                    onChange: e => setSsn4(e.target.value),
                    pattern: {
                      value: /\d{4}/,
                      message: 'Should be 4 numeric digits ',
                    },
                  })}
                />
                <div className="k-form-validation-message">
                  <ErrorMessage errors={errors} name="ssn" render={({ message }) => message} />
                </div>
              </div>
            )}
            <div className="k-field">
              <label htmlFor="subscribetosms" className="subscribe-to-sms">
                <input
                  type="checkbox"
                  name="subscribetosms"
                  className="subscribe-to-sms-input"
                  data-testid="subscribetosms"
                  defaultChecked
                  onClick={() => setSmsMarketing(!smsMarketing)}
                />
                <span className="subscribe-to-sms-text">
                  I would like to receive marketing messages from Katapult through SMS to the phone number provided.
                  Message & data rates apply. Message frequency varies. Reply HELP for support or STOP to cancel. Check
                  out{' '}
                  <a href="https://katapult.com/app/terms-of-use" target="_blank" rel="noreferrer">
                    Terms of use
                  </a>
                </span>
              </label>
            </div>
            <div className="k-field">
              <label htmlFor="subscribetoemail" className="subscribe-to-sms">
                <input
                  type="checkbox"
                  name="subscribetoemail"
                  className="subscribe-to-sms-input subscribe-to-email-input"
                  defaultChecked
                  data-testid="subscribetoemail"
                  onClick={() => setEmailMarketing(!emailMarketing)}
                />
                <span className="subscribe-to-sms-text">
                  I would like to receive marketing messages from Katapult through email.
                </span>
              </label>
            </div>
          </div>
          <SubmitButton
            text={store.shopify?.isReturnUserFlow ? 'Continue' : 'Begin application'}
            loading={loading || shopifyLoading}
          />
        </form>
      </section>
    </div>
  );
}

export default ReturnUserFlow;
